<script setup>
/**
 * Every answer the assistant knows, and the controls to fix one. Ease C3, spec §6 P-6.1.
 *
 * WHY THIS LIST EXISTS AT ALL: the retriever searches these question/answer pairs and never
 * the original text. So when an owner says "it answered wrongly about delivery", this list is
 * the only place the reason is visible, and the only place she can change it without removing
 * a whole source.
 *
 * ONE COMPONENT, TWO PLACES. On Answers it lists everything, with a filter. On one source's
 * page (`documentId`) it lists only what that source taught, and the filter is not shown.
 *
 * TWO SEARCHES, ONE OF THEM PRICED. A words search is free and runs on Enter. A search by
 * meaning is one AI step, so it carries its price on the button and asks once per visit
 * (`useAiConfirm`'s session mode, O-11); a page of meaning results asks the server again with
 * the same price and goes straight through.
 *
 * NOTHING FREE READS BY MEANING. Every meaning read is charged, so the buttons that carry no
 * price never make one: after Switch off / on, Remove or a saved Edit the row changes in place
 * from what the write sent back, and a restore or a new filter reads the list again by words.
 *
 * A LIST THAT COULD NOT BE READ IS NAMED, never rendered as "no answers": an empty source is a
 * real state with a real amber warning, and a timeout must not counterfeit it.
 *
 * Earlier versions (`RestoreHistory`) mount only for the row she opened them on, so a page of
 * 25 rows costs no history reads.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';

import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import AiConfirmDialog from './AiConfirmDialog.vue';
import RestoreHistory from './RestoreHistory.vue';
import TeachAnswerDialog from './TeachAnswerDialog.vue';
import WasloInput from './WasloInput.vue';
import { useWasloI18n } from '../i18n.js';
import { useAiConfirm } from '../composables/useAiConfirm.js';
import { toIntlLocale } from '../helpers/intl.js';
import { wasloApi, withConfirm } from '../api/client.js';

const props = defineProps({
  accountId: { type: Number, default: null },
  /** One source's answers only; `null` lists every answer. */
  documentId: { type: Number, default: null },
});

/** `loaded` carries the page (the source page reads its total); `changed` after any write. */
const emit = defineEmits(['loaded', 'changed']);

const { t, locale } = useWasloI18n();

const FILTERS = ['all', 'yours', 'sources', 'off'];

const meaningCharge = useAiConfirm({
  accountId: computed(() => props.accountId),
  op: 'search_by_meaning',
  session: true,
});

const q = ref('');
const by = ref('words');
const filter = ref('all');
const page = ref(1);
const data = ref(null);
const isFetching = ref(false);
const unavailable = ref(false);
const failure = ref('');
const busy = ref(null);
const historyFor = ref(null);
let controller = null;

const formatCount = value =>
  new Intl.NumberFormat(toIntlLocale(locale.value)).format(value);

const items = computed(() => data.value?.items ?? []);
const total = computed(() => data.value?.total ?? 0);
const pageSize = computed(() => data.value?.pageSize ?? 25);
const lastPage = computed(() =>
  Math.max(1, Math.ceil(total.value / pageSize.value))
);
const showing = computed(() => {
  if (!data.value || total.value <= pageSize.value) return '';
  const from = (page.value - 1) * pageSize.value + 1;
  const to = Math.min(page.value * pageSize.value, total.value);
  return t('WASLO.ALL_ANSWERS.SHOWING', {
    from: formatCount(from),
    to: formatCount(to),
    total: formatCount(total.value),
  });
});
const isSearching = computed(() => q.value.trim() !== '');
const byMeaning = computed(() => isSearching.value && by.value === 'meaning');
// A meaning search charges every page again, so its paging buttons say their price.
const pageLabel = action =>
  byMeaning.value ? meaningCharge.label(action) : action;

const queryOf = () => ({
  q: q.value.trim() || undefined,
  by: isSearching.value ? by.value : undefined,
  filter: props.documentId === null ? filter.value : undefined,
  documentId: props.documentId ?? undefined,
  page: page.value,
});

const load = async () => {
  if (props.accountId === null) return;
  controller?.abort();
  controller = new AbortController();
  const { signal } = controller;
  isFetching.value = true;
  unavailable.value = false;
  try {
    let result;
    if (byMeaning.value) {
      const outcome = await meaningCharge.confirmAndRun({
        run: credits =>
          wasloApi.getAnswers(
            props.accountId,
            queryOf(),
            signal,
            withConfirm(credits)
          ),
      });
      if (outcome.cancelled) {
        by.value = 'words';
        return;
      }
      result = outcome.result;
    } else {
      result = await wasloApi.getAnswers(props.accountId, queryOf(), signal);
    }
    data.value = result;
    emit('loaded', result);
  } catch (error) {
    if (error?.name === 'AbortError') return;
    unavailable.value = true;
  } finally {
    if (!signal.aborted) isFetching.value = false;
  }
};

const searchWords = () => {
  by.value = 'words';
  page.value = 1;
  load();
};
const searchMeaning = () => {
  if (!isSearching.value) return;
  by.value = 'meaning';
  page.value = 1;
  load();
};
const clearSearch = () => {
  q.value = '';
  by.value = 'words';
  page.value = 1;
  load();
};
const goTo = next => {
  if (next < 1 || next > lastPage.value) return;
  page.value = next;
  load();
};

/** Page one again, read for free: a meaning search falls back to words. */
const readFromStart = () => {
  by.value = 'words';
  page.value = 1;
  load();
};

watch(filter, readFromStart);
watch(() => [props.accountId, props.documentId], readFromStart, {
  immediate: true,
});
onBeforeUnmount(() => controller?.abort());

// ── the row's own controls ─────────────────────────────────────────────────────────────
const editRef = ref(null);
const editing = ref(null);

const edit = async row => {
  editing.value = row;
  // The dialog is mounted for the row once `editing` is set; open it on the next tick.
  await nextTick();
  editRef.value?.open();
};

/**
 * After a write. A words list (or no search) simply reads again, free. A meaning list must not:
 * the row changes in place from what the write sent back (`answer`, or `removedId`), and the
 * "Replaced by your answer" badges follow it (P-6.2: hers off, removed or reworded gives the
 * source's answers back; `replaced` names the ones it now replaces). A write with no row to
 * show (a restore) reads again by words.
 */
const afterChange = ({
  answer = null,
  removedId = null,
  replaced = [],
} = {}) => {
  emit('changed');
  if (!byMeaning.value || !data.value) {
    load();
    return;
  }
  if (answer === null && removedId === null) {
    readFromStart();
    return;
  }
  const changedId = answer?.id ?? removedId;
  const nowReplaced = new Set(
    answer?.enabled ? replaced.map(other => other.id) : []
  );
  const rows = data.value.items
    .filter(item => item.id !== removedId)
    .map(item => {
      if (answer !== null && item.id === answer.id) return answer;
      if (nowReplaced.has(item.id)) return { ...item, replacedById: changedId };
      if (item.replacedById === changedId)
        return { ...item, replacedById: null };
      return item;
    });
  data.value = {
    ...data.value,
    items: rows,
    total: data.value.total - (data.value.items.length - rows.length),
  };
};

const toggle = async row => {
  if (busy.value !== null || props.accountId === null) return;
  busy.value = row.id;
  failure.value = '';
  try {
    const result = await wasloApi.updateAnswer(props.accountId, row.id, {
      enabled: !row.enabled,
    });
    afterChange(result);
  } catch {
    failure.value = t('WASLO.ALL_ANSWERS.FAILED');
  } finally {
    busy.value = null;
  }
};

const remove = async row => {
  if (busy.value !== null || props.accountId === null) return;
  busy.value = row.id;
  failure.value = '';
  try {
    await wasloApi.removeAnswer(props.accountId, row.id);
    failure.value = t('WASLO.ALL_ANSWERS.REMOVED');
    afterChange({ removedId: row.id });
  } catch {
    failure.value = t('WASLO.ALL_ANSWERS.FAILED');
  } finally {
    busy.value = null;
  }
};

const toggleHistory = row => {
  historyFor.value = historyFor.value === row.id ? null : row.id;
};

const filterLabel = id => t(`WASLO.ALL_ANSWERS.FILTER.${id.toUpperCase()}`);
</script>

<template>
  <div class="flex flex-col gap-3" data-waslo-answers>
    <form
      class="flex flex-col gap-2 sm:flex-row sm:items-end"
      data-waslo-answers-search
      @submit.prevent="searchWords"
    >
      <WasloInput
        v-model="q"
        class="flex-1"
        :label="t('WASLO.ALL_ANSWERS.SEARCH_LABEL')"
        :placeholder="t('WASLO.ALL_ANSWERS.SEARCH_PLACEHOLDER')"
      />
      <div class="flex flex-wrap gap-2">
        <Button
          type="submit"
          size="sm"
          :label="t('WASLO.ALL_ANSWERS.SEARCH_WORDS')"
          data-waslo-answers-search-words
        />
        <Button
          type="button"
          size="sm"
          variant="faded"
          color="slate"
          :disabled="!isSearching"
          :label="meaningCharge.label(t('WASLO.ALL_ANSWERS.SEARCH_MEANING'))"
          data-waslo-answers-search-meaning
          @click="searchMeaning"
        />
        <Button
          v-if="isSearching"
          type="button"
          size="sm"
          variant="ghost"
          color="slate"
          :label="t('WASLO.ALL_ANSWERS.CLEAR')"
          @click="clearSearch"
        />
      </div>
    </form>

    <div
      v-if="documentId === null"
      class="flex flex-wrap gap-2"
      role="radiogroup"
      data-waslo-answers-filter
    >
      <Button
        v-for="id in FILTERS"
        :key="id"
        size="xs"
        :variant="filter === id ? 'solid' : 'faded'"
        :color="filter === id ? 'blue' : 'slate'"
        :label="filterLabel(id)"
        role="radio"
        :aria-checked="filter === id"
        :data-waslo-answers-filter-option="id"
        @click="filter = id"
      />
    </div>

    <Banner v-if="unavailable" color="slate" data-waslo-source-unavailable>
      {{
        documentId === null
          ? t('WASLO.ALL_ANSWERS.UNAVAILABLE')
          : t('WASLO.ANSWERS.SOURCE.ANSWERS_UNAVAILABLE')
      }}
    </Banner>

    <div
      v-else-if="data && !items.length && documentId !== null && !isSearching"
      class="flex items-start w-full gap-4 px-6 py-5 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
      data-waslo-answers-none
    >
      <span
        class="grid shrink-0 place-items-center size-9 rounded-xl bg-n-amber-3"
        aria-hidden="true"
      >
        <span class="size-5 i-lucide-file-question text-n-amber-11" />
      </span>
      <p class="mb-0 text-sm leading-6 text-n-slate-11">
        {{ t('WASLO.ANSWERS.SOURCE.ANSWERS_NONE') }}
      </p>
    </div>

    <p
      v-else-if="data && !items.length"
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-answers-empty-list
    >
      {{
        isSearching || filter !== 'all'
          ? t('WASLO.ALL_ANSWERS.NONE')
          : t('WASLO.ALL_ANSWERS.EMPTY')
      }}
    </p>

    <ul
      v-else-if="items.length"
      class="flex flex-col w-full divide-y outline outline-1 -outline-offset-1 divide-n-weak outline-n-container rounded-xl bg-n-solid-2"
      :aria-busy="isFetching"
      data-waslo-answers-list
    >
      <li
        v-for="item in items"
        :key="item.id"
        class="flex flex-col gap-2 px-5 py-4"
        :class="{ 'opacity-70': !item.enabled || item.replacedById !== null }"
        :data-waslo-answer-row="item.id"
      >
        <div class="flex flex-wrap items-center gap-2 text-xs">
          <span
            v-if="item.mine"
            class="px-2 py-0.5 rounded-md bg-n-blue-3 text-n-blue-11"
            data-waslo-answer-mine
          >
            {{ t('WASLO.ALL_ANSWERS.YOURS') }}
          </span>
          <span
            v-if="!item.enabled"
            class="px-2 py-0.5 rounded-md bg-n-slate-3 text-n-slate-11"
            data-waslo-answer-off
          >
            {{ t('WASLO.ALL_ANSWERS.OFF') }}
          </span>
          <span
            v-if="item.replacedById !== null"
            class="px-2 py-0.5 rounded-md bg-n-slate-3 text-n-slate-11"
            data-waslo-answer-replaced
          >
            {{ t('WASLO.ALL_ANSWERS.REPLACED') }}
          </span>
          <span
            v-if="documentId === null && !item.mine"
            class="text-n-slate-10"
            dir="auto"
          >
            {{ t('WASLO.ALL_ANSWERS.FROM', { source: item.sourceTitle }) }}
          </span>
        </div>
        <!-- Both halves are text somebody else wrote, so each resolves its own direction. -->
        <p
          class="mb-0 text-sm font-medium break-words text-n-slate-12"
          dir="auto"
          data-waslo-answer-question
        >
          {{ item.question }}
        </p>
        <p
          class="mb-0 text-sm leading-6 break-words whitespace-pre-line text-n-slate-11"
          dir="auto"
          data-waslo-answer-answer
        >
          {{ item.answer }}
        </p>
        <div class="flex flex-wrap gap-2">
          <Button
            size="xs"
            variant="faded"
            color="slate"
            :label="t('WASLO.ALL_ANSWERS.EDIT')"
            data-waslo-answer-edit
            @click="edit(item)"
          />
          <Button
            size="xs"
            variant="faded"
            color="slate"
            :is-loading="busy === item.id"
            :label="
              item.enabled
                ? t('WASLO.ALL_ANSWERS.SWITCH_OFF')
                : t('WASLO.ALL_ANSWERS.SWITCH_ON')
            "
            data-waslo-answer-toggle
            @click="toggle(item)"
          />
          <Button
            size="xs"
            variant="ghost"
            color="slate"
            :label="t('WASLO.ALL_ANSWERS.HISTORY')"
            :aria-expanded="historyFor === item.id"
            data-waslo-answer-history
            @click="toggleHistory(item)"
          />
          <Button
            size="xs"
            variant="ghost"
            color="ruby"
            :label="t('WASLO.ALL_ANSWERS.REMOVE')"
            data-waslo-answer-remove
            @click="remove(item)"
          />
        </div>
        <RestoreHistory
          v-if="historyFor === item.id"
          :account-id="accountId"
          kind="answer"
          :item-id="item.id"
          @restored="afterChange()"
        />
      </li>
    </ul>

    <p
      v-if="failure"
      class="mb-0 text-sm text-n-slate-11"
      aria-live="polite"
      data-waslo-answers-said
    >
      {{ failure }}
    </p>

    <div
      v-if="showing"
      class="flex flex-wrap items-center gap-3"
      data-waslo-answers-paging
    >
      <p class="mb-0 text-sm text-n-slate-10">{{ showing }}</p>
      <Button
        size="xs"
        variant="faded"
        color="slate"
        :disabled="page <= 1 || isFetching"
        :label="pageLabel(t('WASLO.ALL_ANSWERS.PREVIOUS'))"
        data-waslo-answers-previous
        @click="goTo(page - 1)"
      />
      <Button
        size="xs"
        variant="faded"
        color="slate"
        :disabled="page >= lastPage || isFetching"
        :label="pageLabel(t('WASLO.ALL_ANSWERS.NEXT'))"
        data-waslo-answers-next
        @click="goTo(page + 1)"
      />
    </div>

    <TeachAnswerDialog
      v-if="editing"
      :key="editing.id"
      ref="editRef"
      mode="edit"
      :account-id="accountId"
      :answer-id="editing.id"
      :suggested-question="editing.question"
      :initial-answer="editing.answer"
      @saved="afterChange"
    />
    <AiConfirmDialog :controller="meaningCharge" />
  </div>
</template>
